import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ZoomInIcon, XIcon } from 'lucide-react';
import { useBackNavigation } from '../hooks/useBackNavigation';
export function ANMWebsite() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const goBack = useBackNavigation('/ux-ui-design');
  return <div className="w-full min-h-screen bg-white" data-id="element-237">
      {/* Image Modal */}
      {selectedImage && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" onClick={() => setSelectedImage(null)} data-id="element-238">
          <button onClick={() => setSelectedImage(null)} className="absolute top-4 right-4 text-white hover:text-emerald-400 transition-colors" data-id="element-239">
            <XIcon className="w-8 h-8" data-id="element-240" />
          </button>
          <motion.img initial={{
        scale: 0.9
      }} animate={{
        scale: 1
      }} src={selectedImage} alt="Enlarged view" className="max-w-full max-h-full object-contain" data-id="element-241" />
        </motion.div>}
      {/* Header */}
      <header className="py-8 px-6 bg-white border-b border-stone-200 sticky top-0 z-40" data-id="element-242">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-243">
          <button onClick={goBack} className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors" data-id="element-244">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-245" />
            <span className="font-light" data-id="element-246">Back</span>
          </button>
          <h1 className="text-2xl font-light text-stone-900" data-id="element-247">Maria Elina</h1>
        </div>
      </header>
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-stone-50 via-emerald-50/30 to-stone-50" data-id="element-248">
        <div className="max-w-5xl mx-auto" data-id="element-249">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-250">
            <p className="text-emerald-600 font-light tracking-wide uppercase mb-4" data-id="element-251">
              UX/UI Design
            </p>
            <h1 className="text-4xl md:text-6xl font-light text-stone-900 mb-6" data-id="element-252">
              ANM Website
            </h1>
            <p className="text-xl text-stone-600 font-light mb-8" data-id="element-253">
              Website re-design and user experience proposition for
              Aleksandro-Nevsky Manufacture
            </p>
            <div className="space-y-4 text-stone-700 font-light" data-id="element-254">
              <p className="text-lg" data-id="element-255">
                <span className="font-normal text-stone-900" data-id="element-256">Role:</span> UX/UI
                Designer, Web Designer
              </p>
              <p className="text-lg" data-id="element-257">
                <span className="font-normal text-stone-900" data-id="element-258">Client:</span>{' '}
                Aleksandro-Nevsky Manufacture
              </p>
              <p className="text-lg" data-id="element-259">
                <span className="font-normal text-stone-900" data-id="element-260">Platform:</span>{' '}
                Web (Desktop & Mobile)
              </p>
              <p className="text-lg" data-id="element-261">
                <span className="font-normal text-stone-900" data-id="element-262">Year:</span> 2023
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Project Overview */}
      <section className="py-20 px-6 bg-white" data-id="element-263">
        <div className="max-w-5xl mx-auto" data-id="element-264">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-265">
            <h2 className="text-3xl font-light text-stone-900 mb-6" data-id="element-266">
              Project Overview
            </h2>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-267">
              To accompany the kick-off of a new commercial building
              development, Aleksandro-Nevsky Manufacture is undergoing a
              comprehensive re-branding process. As part of this transformation,
              I was tasked with proposing a new website that would elevate the
              company's brand image and better suit the new Class-A business
              center addition.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-268">
              The website needed to reflect the premium nature of the
              development while maintaining accessibility and clarity for
              potential tenants, investors, and partners. The design balances
              corporate professionalism with the historic character of the
              renovated industrial complex.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed" data-id="element-269">
              The responsive design ensures seamless access across all devices,
              with particular attention to mobile optimization for on-the-go
              property searches and inquiries.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Website Design - Desktop & Mobile */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-270">
        <div className="max-w-7xl mx-auto" data-id="element-271">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-272">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-273">
              Final Website Design
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-274">
              A comprehensive digital presence showcasing the business center's
              amenities, available spaces, and architectural heritage across all
              devices.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 items-start" data-id="element-275">
              {/* Desktop - Takes 3 columns */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="md:col-span-3 group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%201%20(1).png')} data-id="element-276">
                <div className="relative overflow-hidden rounded-2xl shadow-xl bg-stone-900" data-id="element-277">
                  <img src="https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%201%20(1).png" alt="ANM Desktop Website" className="w-full h-auto object-contain" data-id="element-278" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-279">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-280" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-281">
                  <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-282">
                    Desktop Experience
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-283">
                    Full website layout showcasing the business center's spaces
                    and amenities
                  </p>
                </div>
              </motion.div>
              {/* Mobile - Takes 1 column */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="md:col-span-1 group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/anm_web_ready/%D0%B0%D0%BD%D0%BC%20%D0%BC%D0%BE%D0%B1%D0%B8%D0%BB%20full%20screen.png')} data-id="element-284">
                <div className="relative overflow-hidden rounded-2xl shadow-xl bg-stone-900" data-id="element-285">
                  <img src="https://storage.yandexcloud.net/maria-images/images/anm_web_ready/%D0%B0%D0%BD%D0%BC%20%D0%BC%D0%BE%D0%B1%D0%B8%D0%BB%20full%20screen.png" alt="ANM Mobile Website" className="w-full h-auto object-contain" data-id="element-286" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-287">
                    <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-288" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-289">
                  <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-290">
                    Mobile Experience
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-291">
                    Optimized responsive layout for mobile access
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Interactive Flow - Rent a Facility */}
      <section className="py-20 px-6 bg-white" data-id="element-292">
        <div className="max-w-7xl mx-auto" data-id="element-293">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-294">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-295">
              Interactive Experience: Rent a Facility
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-296">
              The website features an interactive flow that guides potential
              tenants through the facility rental process, from browsing
              available spaces to submitting inquiries.
            </p>
            {/* Starter Image */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer mb-8" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%202.png')} data-id="element-297">
              <div className="relative overflow-hidden rounded-2xl shadow-xl bg-stone-900" data-id="element-298">
                <img src="https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%202.png" alt="Interactive facility selection starting point" className="w-full h-auto object-contain" data-id="element-299" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-300">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-301" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-302">
                Starting point: Facility overview with interactive selection
              </p>
            </motion.div>
            {/* Path A: Spaces Available */}
            <div className="mb-16" data-id="element-303">
              <h3 className="text-2xl font-light text-stone-900 mb-3" data-id="element-304">
                Path A: Spaces Available
                <span className="ml-3 text-sm text-emerald-600 font-normal" data-id="element-305">
                  Direct Inquiry
                </span>
              </h3>
              <p className="text-stone-600 font-light mb-8 max-w-3xl" data-id="element-306">
                When spaces are available, users can browse options, view
                detailed information, and submit inquiries directly.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-307">
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%2010.png')} data-id="element-308">
                  <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900" data-id="element-309">
                    <img src="https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%2010.png" alt="Available spaces overview" className="w-full h-auto object-contain" data-id="element-310" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-311">
                      <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-312" />
                    </div>
                  </div>
                  <p className="text-stone-600 font-light mt-3 text-sm" data-id="element-313">
                    Step 1: Browse available spaces with interactive building
                    view
                  </p>
                </motion.div>
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.1
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%2011.png')} data-id="element-314">
                  <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900" data-id="element-315">
                    <img src="https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%2011.png" alt="Space details modal" className="w-full h-auto object-contain" data-id="element-316" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-317">
                      <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-318" />
                    </div>
                  </div>
                  <p className="text-stone-600 font-light mt-3 text-sm" data-id="element-319">
                    Step 2: Quick preview modal with space details
                  </p>
                </motion.div>
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.2
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%2012.png')} data-id="element-320">
                  <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900" data-id="element-321">
                    <img src="https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%2012.png" alt="Office details page" className="w-full h-auto object-contain" data-id="element-322" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-323">
                      <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-324" />
                    </div>
                  </div>
                  <p className="text-stone-600 font-light mt-3 text-sm" data-id="element-325">
                    Step 3: Full office details page with inquiry option
                  </p>
                </motion.div>
              </div>
            </div>
            {/* Path B: No Spaces Available */}
            <div data-id="element-326">
              <h3 className="text-2xl font-light text-stone-900 mb-3" data-id="element-327">
                Path B: No Spaces Available
                <span className="ml-3 text-sm text-stone-600 font-normal" data-id="element-328">
                  Waitlist Signup
                </span>
              </h3>
              <p className="text-stone-600 font-light mb-8 max-w-3xl" data-id="element-329">
                When all spaces are occupied, users can join a waitlist to be
                notified when new spaces become available.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-330">
                {/* Left Column */}
                <div className="space-y-6" data-id="element-331">
                  <motion.div initial={{
                  opacity: 0,
                  y: 30
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.6
                }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%204.png')} data-id="element-332">
                    <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900" data-id="element-333">
                      <img src="https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%204.png" alt="Step 1.1: Press to select a building of interest" className="w-full h-auto object-contain" data-id="element-334" />
                      <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-335">
                        <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-336" />
                      </div>
                    </div>
                    <p className="text-stone-600 font-light mt-3 text-sm" data-id="element-337">
                      Step 1.1: Press to select a building of interest
                    </p>
                  </motion.div>
                  <motion.div initial={{
                  opacity: 0,
                  y: 30
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.6,
                  delay: 0.1
                }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%206.png')} data-id="element-338">
                    <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900" data-id="element-339">
                      <img src="https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%206.png" alt="Step 2.1: Contact form to receive availability updates" className="w-full h-auto object-contain" data-id="element-340" />
                      <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-341">
                        <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-342" />
                      </div>
                    </div>
                    <p className="text-stone-600 font-light mt-3 text-sm" data-id="element-343">
                      Step 2.1: Contact form to receive availability updates
                    </p>
                  </motion.div>
                  <motion.div initial={{
                  opacity: 0,
                  y: 30
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.6,
                  delay: 0.2
                }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%208.png')} data-id="element-344">
                    <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900" data-id="element-345">
                      <img src="https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%208.png" alt="Step 3.1: Confirmation of inquiry submission" className="w-full h-auto object-contain" data-id="element-346" />
                      <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-347">
                        <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-348" />
                      </div>
                    </div>
                    <p className="text-stone-600 font-light mt-3 text-sm" data-id="element-349">
                      Step 3.1: Confirmation of inquiry submission
                    </p>
                  </motion.div>
                </div>
                {/* Right Column */}
                <div className="space-y-6" data-id="element-350">
                  <motion.div initial={{
                  opacity: 0,
                  y: 30
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.6,
                  delay: 0.3
                }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%205.png')} data-id="element-351">
                    <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900" data-id="element-352">
                      <img src="https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%205.png" alt="Step 1.2: Press to select alternative building" className="w-full h-auto object-contain" data-id="element-353" />
                      <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-354">
                        <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-355" />
                      </div>
                    </div>
                    <p className="text-stone-600 font-light mt-3 text-sm" data-id="element-356">
                      Step 1.2: Press to select alternative building
                    </p>
                  </motion.div>
                  <motion.div initial={{
                  opacity: 0,
                  y: 30
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.6,
                  delay: 0.4
                }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%207.png')} data-id="element-357">
                    <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900" data-id="element-358">
                      <img src="https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%207.png" alt="Step 2.2: Contact form to receive updates for alternative building" className="w-full h-auto object-contain" data-id="element-359" />
                      <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-360">
                        <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-361" />
                      </div>
                    </div>
                    <p className="text-stone-600 font-light mt-3 text-sm" data-id="element-362">
                      Step 2.2: Contact form to receive updates for alternative
                      building
                    </p>
                  </motion.div>
                  <motion.div initial={{
                  opacity: 0,
                  y: 30
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.6,
                  delay: 0.5
                }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%209.png')} data-id="element-363">
                    <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900" data-id="element-364">
                      <img src="https://storage.yandexcloud.net/maria-images/images/anm_web_ready/MacBook%20Pro%2014_%20-%209.png" alt="Step 3.2: Confirmation of inquiry submission" className="w-full h-auto object-contain" data-id="element-365" />
                      <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-366">
                        <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-367" />
                      </div>
                    </div>
                    <p className="text-stone-600 font-light mt-3 text-sm" data-id="element-368">
                      Step 3.2: Confirmation of inquiry submission
                    </p>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Mobile User Journey - Moved below Interactive Experience */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-369">
        <div className="max-w-7xl mx-auto" data-id="element-370">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-371">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-372">
              Mobile User Journey
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-373">
              The mobile experience guides users through a streamlined journey
              from facility selection to detailed space information.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-374">
              {/* Step 1: Facility Selection */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/anm_web_ready/%D0%B0%D0%BD%D0%BC%20%D0%BC%D0%BE%D0%B1%D0%B8%D0%BB%206.png')} data-id="element-375">
                <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900" data-id="element-376">
                  <img src="https://storage.yandexcloud.net/maria-images/images/anm_web_ready/%D0%B0%D0%BD%D0%BC%20%D0%BC%D0%BE%D0%B1%D0%B8%D0%BB%206.png" alt="Mobile facility selection" className="w-full h-auto object-contain" data-id="element-377" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-378">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-379" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-380">
                  <h5 className="text-sm font-light text-stone-900 mb-1" data-id="element-381">
                    Step 1: Facility Selection
                  </h5>
                  <p className="text-stone-600 font-light text-xs" data-id="element-382">
                    Interactive building selection
                  </p>
                </div>
              </motion.div>
              {/* Step 2: Building Overview */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/anm_web_ready/%D0%B0%D0%BD%D0%BC%20%D0%BC%D0%BE%D0%B1%D0%B8%D0%BB%2010.png')} data-id="element-383">
                <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900" data-id="element-384">
                  <img src="https://storage.yandexcloud.net/maria-images/images/anm_web_ready/%D0%B0%D0%BD%D0%BC%20%D0%BC%D0%BE%D0%B1%D0%B8%D0%BB%2010.png" alt="Mobile building overview" className="w-full h-auto object-contain" data-id="element-385" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-386">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-387" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-388">
                  <h5 className="text-sm font-light text-stone-900 mb-1" data-id="element-389">
                    Step 2: Building Overview
                  </h5>
                  <p className="text-stone-600 font-light text-xs" data-id="element-390">
                    Detailed building information
                  </p>
                </div>
              </motion.div>
              {/* Step 3: Space Listing */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/anm_web_ready/%D0%B0%D0%BD%D0%BC%20%D0%BC%D0%BE%D0%B1%D0%B8%D0%BB%207.png')} data-id="element-391">
                <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900" data-id="element-392">
                  <img src="https://storage.yandexcloud.net/maria-images/images/anm_web_ready/%D0%B0%D0%BD%D0%BC%20%D0%BC%D0%BE%D0%B1%D0%B8%D0%BB%207.png" alt="Mobile space listing" className="w-full h-auto object-contain" data-id="element-393" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-394">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-395" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-396">
                  <h5 className="text-sm font-light text-stone-900 mb-1" data-id="element-397">
                    Step 3: Available Spaces
                  </h5>
                  <p className="text-stone-600 font-light text-xs" data-id="element-398">
                    Space specifications and floor plan
                  </p>
                </div>
              </motion.div>
              {/* Step 4: Detailed Space View */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/anm_web_ready/%D0%B0%D0%BD%D0%BC%20%D0%BC%D0%BE%D0%B1%D0%B8%D0%BB%209.png')} data-id="element-399">
                <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900" data-id="element-400">
                  <img src="https://storage.yandexcloud.net/maria-images/images/anm_web_ready/%D0%B0%D0%BD%D0%BC%20%D0%BC%D0%BE%D0%B1%D0%B8%D0%BB%209.png" alt="Mobile detailed space view" className="w-full h-auto object-contain" data-id="element-401" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-402">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-403" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-404">
                  <h5 className="text-sm font-light text-stone-900 mb-1" data-id="element-405">
                    Step 4: Detailed Information
                  </h5>
                  <p className="text-stone-600 font-light text-xs" data-id="element-406">
                    Complete space details and inquiry
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Design Approach */}
      <section className="py-20 px-6 bg-white" data-id="element-407">
        <div className="max-w-5xl mx-auto" data-id="element-408">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-409">
            <h2 className="text-3xl font-light text-stone-900 mb-8" data-id="element-410">
              Design Approach
            </h2>
            <div className="space-y-6 text-lg text-stone-600 font-light leading-relaxed" data-id="element-411">
              <p data-id="element-412">
                The design strategy focused on creating a premium digital
                experience that mirrors the quality of the physical spaces. The
                visual language combines clean, modern aesthetics with subtle
                references to the industrial heritage of the complex.
              </p>
              <p data-id="element-413">
                Typography and spacing were carefully considered to ensure
                readability and elegance, while the color palette—featuring deep
                reds reminiscent of the historic brick facades—creates a strong
                connection to the physical architecture.
              </p>
              <p data-id="element-414">
                The responsive design ensures that whether viewed on a desktop
                during office hours or on mobile while touring the property, the
                experience remains consistent, professional, and engaging.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}